// KPI Builder — how the answers change when the admin picks something in an earlier step.
import { RECIPES, SOURCES } from 'constants/kpiBuilderCatalogue';
import { visibleGroups } from './learnerGroups';
import { blankQuery } from './advanced';
import { PROGRESS_KINDS } from './progressRecipes';
import { LIVE_TIME_RANGES, READY_KINDS } from './readyRecipes';
import { BuilderAnswers, ProgressKind, ReadyKind, RecipeId, SourceId } from './types';

/** Drop learner groups that no longer apply to the chosen data. */
const pruneGroups = (a: BuilderAnswers): BuilderAnswers => {
  const ids = visibleGroups(a).map((g) => g.id);
  return { ...a, segs: a.segs.filter((s) => ids.includes(s)) };
};

const STARTER_SETTINGS = [{ key: 'aggregationPaths', value: '[]' }, { key: 'valueType', value: 'number' }, { key: 'isDecimal', value: 'false' }, { key: 'drillChart', value: 'none' }];

/** Changing the question resets everything that depended on it. */
export function chooseRecipe(a: BuilderAnswers, id: RecipeId): BuilderAnswers {
  if (a.recipe === id) return a;
  const reset = { ...a, recipe: id, src: null, srcB: null, measure: null, group: null, rateSeg: null, chart: null, drill: 'none', kpiFilters: [], segs: [] };
  // A KPI built part by part starts with one empty query and the settings every KPI has.
  if (id === 'advanced') return { ...reset, advanced: { queries: [blankQuery()] }, extras: { ...reset.extras, settings: STARTER_SETTINGS } };
  // A question that can only use one data source picks it for you.
  const only = RECIPES.find((r) => r.id === id)?.sources;
  return only?.length === 1 ? chooseSource(reset, only[0]) : reset;
}

/** Changing the (first) data source resets measure, grouping, columns and filters; one measure is picked for you. */
export function chooseSource(a: BuilderAnswers, id: SourceId): BuilderAnswers {
  if (a.src === id) return a;
  const only = SOURCES[id].measures.length === 1 ? SOURCES[id].measures[0].id : null;
  const srcB = a.srcB === id ? null : a.srcB;
  return pruneGroups({ ...a, src: id, srcB, measure: only, group: null, rateSeg: null, cols: ['name', 'district'], kpiFilters: [] });
}

/** Second list for "in A but not in B". */
export const chooseSourceB = (a: BuilderAnswers, id: SourceId): BuilderAnswers => pruneGroups({ ...a, srcB: id });

/** Add or remove an id from a list. */
export const toggleInList = (list: string[], id: string): string[] => (list.includes(id) ? list.filter((v) => v !== id) : [...list, id]);

/** The progress question fixes both data sources, so choosing the comparison picks them. */
export const chooseProgress = (a: BuilderAnswers, kind: ProgressKind): BuilderAnswers =>
  pruneGroups({ ...a, progressKind: kind, src: PROGRESS_KINDS[kind].src, srcB: PROGRESS_KINDS[kind].srcB, segs: a.segs, measure: null, group: kind === 'course' ? 'competency' : null, kpiFilters: [] });

const READY_TOP_N: Record<ReadyKind, number> = { dropout: 20, reattempt: 10, attempts: 100, facility: 10, histogram: 10, levelFunnel: 10, completedFunnel: 10 };

/** A ready-made chart fixes its own data sources, so choosing it picks them and starts from the live KPI's settings. */
export const chooseReady = (a: BuilderAnswers, kind: ReadyKind): BuilderAnswers => {
  const info = READY_KINDS[kind];
  const custom = kind === 'histogram' ? { ...a.custom, rangeField: 'totalTimeDuration', ranges: LIVE_TIME_RANGES } : a.custom;
  // The completed funnel starts with “Completed the course” ticked; the admin can untick it or add a cadre.
  const segs = kind === 'completedFunnel' ? ['completedEnr'] : a.segs;
  return pruneGroups({ ...a, segs, readyKind: kind, src: info.src, srcB: info.srcB, measure: null, group: kind === 'dropout' ? 'competency' : null, topN: READY_TOP_N[kind], rateSeg: null, custom, kpiFilters: [] });
};
